import { useTexture } from "@react-three/drei";
import { memo, useMemo, useRef, useState } from "react";
import { Color, RepeatWrapping } from "three";

// import { useTheme } from "styled-components";
// TODO: Cant import custom here
import { useScrollbar } from "@14islands/r3f-scroll-rig";
import { useFrame } from "@react-three/fiber";
import { Box, Flex } from "@react-three/flex";

import { events } from "@/common/constants";
import { fullscreenTriangle } from "@/mono/lib/gl";
import useAnimation from "@/mono/lib/hooks/useAnimation";
import emitter from "@/mono/lib/emitter";
import { combinationSDF, range } from "@/mono/lib/gl/shaderChunks";
import useEvent from "@/mono/lib/hooks/useEvent";
import useLoop from "@/mono/lib/hooks/useLoop";
import { map } from "@/mono/lib/math";
import { EASE_CUSTOM_3 } from "mono/lib/styles/easing";

import noise from "@/assets/colored_noise_512.jpg";
import trans from "@/assets/transition.jpeg";
//import { useThemeTransitionGLUniforms } from "@/lib/useThemeTransitionGL";
import GLText from "@/mono/components/gl/GLText";

const overlayDuration = 500;

export const TRANSITION_DURATION = 800;

const fragmentShader = /* glsl */ `
  varying vec2 vUv;

  uniform float uTransition;
  uniform float uTime;
  uniform float uDirection;
  uniform vec3 uBackgroundColor;
  uniform vec3 uBorderColor;
  uniform sampler2D tNoise;
  uniform sampler2D tTransition;

  ${combinationSDF}
  ${range}

  float swirl(sampler2D tex, vec2 st, vec2 scale, vec2 speed, float smoothness, bool invertXY) {
      vec2 uv0 = st;
      vec2 uv1 = st;
      if(invertXY == true) {
          uv0 = (st * scale.x) + vec2(uTime * speed.x, 0.);
          uv1 = (st * scale.y) + vec2(0.0, -uTime * speed.y);
      } else {
          uv0 = (st * scale.x) + vec2(uTime * speed.x);
          uv1 = (st * scale.y) - vec2(-uTime * speed.y);
      }

      vec4 sw1 = texture2D(tex, uv0);
      vec4 sw2 = texture2D(tex, uv1);

      return opSmoothIntersection(sw1.r, sw2.r, smoothness);
  }

  void main() {
      

      vec2 uv = vUv;
      uv.y -= uTime * 0.1;
      
      // float n = swirl(tTransition, uv, vec2(1.1, 2.2), vec2(0.2, 0.1), 0.5, false);
      float n = texture2D(tTransition, uv * 4.).r;
      float n2 = texture2D(tTransition, uv * 10.).r;
      // float nt = swirl(tNoise, uv, vec2(n, n), vec2(0.2, 0.1), 0.5, false);

      
      float tn = 1. - uTransition;

      //n *= n2;
      
      //TEMP:
      // float dir = 1. - vUv.y * (n);
      float dir = 1. - vUv.y;
      if(uDirection == -1.0) {
        // dir = vUv.y * (n);
        dir = vUv.y;
      }

      float bth = 0.1;
      float a = step(dir, uTransition);
      // float a2 = step(dir, uTransition - bth);

      // float th = 0.2 - (nt * 0.1);
      // float th = 0.1;
      float th = 0.01;
      float edge0 = uTransition - th;
      float edge1 = uTransition;      

      // float border = smoothstep(edge0, edge1, dir) * (1.0 - smoothstep(edge1, edge1 + th, dir));
      //float border = smoothstep(edge0, edge1, dir) * (1.0 - smoothstep(edge1, edge1 + th, dir));
      float border = step(edge0, dir) - step(edge1, dir);

      vec3 color = uBackgroundColor;

      color = mix(color, uBorderColor, border);

      float finalT = crange(uTransition, 0.2, 1.0, 0.0, 1.0);
      color = mix(color, uBackgroundColor, uTransition);
      
      gl_FragColor = vec4(color, a);
  }
`;

const height = 2.4;

const bgColorLight = "#2B393B";
const bgColorDark = "#232323";
const bgColorAccentLight = "#00FFC2";
const bgColorAccentDark = "#376A5D";

const colors = {
  light: {
    background: bgColorLight,
    accent: bgColorAccentLight,
  },
  dark: {
    background: bgColorDark,
    accent: bgColorAccentDark,
  },
};
export default memo(function GLBackground() {
  const geometry = useMemo(() => {
    return fullscreenTriangle;
  }, []);

  const textGroup = useRef();
  const bgTexts = useRef([]);
  const bgTextGroups = useRef([]);

  const { __lenis: lenis } = useScrollbar();

  const [tNoise, tTransition] = useTexture([noise.src, trans.src]);
  tNoise.wrapS = tNoise.wrapT = RepeatWrapping;
  tTransition.wrapS = tTransition.wrapT = RepeatWrapping;

  const isVisible = useRef(false);

  const [theme, setTheme] = useState("light");

  const { backgroundColor, textColor } = useMemo(() => {
    return {
      backgroundColor: new Color(
        colors[theme].background
      ).convertLinearToSRGB(),
      textColor: new Color(colors[theme].accent).convertLinearToSRGB(),
    };
  }, [theme]);

  const visible = useRef(false);
  const textVisible = useRef(false);

  const group = useRef();

  const shader = useMemo(
    () => ({
      depthWrite: false,
      depthTest: false,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
    
        void main() {
            gl_Position = vec4(position, 1.0);
            vUv = uv;
        }
      `,
      fragmentShader,
      uniforms: {
        uTransition: { value: 0 },
        uBackgroundColor: {
          value: backgroundColor,
          //__colorName: "menuBackground",
        },
        uBorderColor: {
          value: textColor,
          //__colorName: "accent",
        },
        uTime: {
          value: 0,
        },
        uDirection: {
          value: 1,
        },
        tNoise: {
          value: tNoise,
        },
        tTransition: {
          value: tTransition,
        },
      },
      transparent: true,
    }),
    [theme]
  );

  const bg = useRef();
  // useThemeTransitionGLUniforms(
  //   () => bg.current,
  //   ["uBackgroundColor", "uBorderColor"],
  //   shader.uniforms
  // );
  const textUniforms = useMemo(
    () => ({
      uColor: {
        value: textColor,
        __colorName: "menuBackgroundText",
      },
    }),
    []
  );
  // useThemeTransitionGLUniforms(
  //   () => bgTexts.current[0].mesh,
  //   ["uColor"],
  //   textUniforms
  // );
  // useThemeTransitionGLUniforms(
  //   () => bgTexts.current[1].mesh,
  //   ["uColor"],
  //   textUniforms
  // );
  // useThemeTransitionGLUniforms(
  //   () => bgTexts.current[2].mesh,
  //   ["uColor"],
  //   textUniforms
  // );

  // useEffect(() => {
  //   const textPromises = bgTexts.current.map((text) => text.isSynced());

  //   async function setDimensions() {
  //     await Promise.all(textPromises);

  //     let totalHeight = 0;
  //     bgTexts.current.forEach(async (text, i) => {
  //       const bb = text.getBoundingBox();

  //       const width = bb.max.x - bb.min.x;
  //       bgTextGroups.current[i].__width = width;

  //       const height = bb.max.y - bb.min.y;

  //       // FIXME:
  //       totalHeight += height - 0.05;
  //     });

  //     textGroup.current.position.y = totalHeight / 4;
  //   }

  //   setDimensions();
  // }, []);

  const { animateIn: animateBackgroundIn, animateOut: animateBackgroundOut } =
    useAnimation({
      inParams: {
        duration: TRANSITION_DURATION / 1000,
        delay: overlayDuration / 4000,
        ease: EASE_CUSTOM_3,
        onStart: () => {
          isVisible.current = true;
          shader.uniforms.uDirection.value = 1;
          shader.uniforms.uTransition.value = 0;

          //menuStore.setState({ isOpen: true });
        },
        onUpdate: (v) => {
          shader.uniforms.uTransition.value = v;
        },
        // shouldCancel: () => {
        //   return isVisible.current;
        // },
        onComplete: () => {
          self.postMessage({ type: "animateGLBackgroundInComplete" });
        },
      },
      outParams: {
        duration: TRANSITION_DURATION / 1000,
        ease: EASE_CUSTOM_3,
        //event: events.MENU_CLOSE,
        onStart: () => {
          isVisible.current = false;
          shader.uniforms.uTransition.value = 0;
          shader.uniforms.uDirection.value = -1;
          //shader.uniforms.uTransition.value = 0;
        },
        onUpdate: (v) => {
          shader.uniforms.uTransition.value = v;
        },
        // shouldCancel: () => {
        //   return !isVisible.current;
        // },
        onComplete: () => {
          // emitter.emit(events.MENU_OUT_COMPLETE);
          self.postMessage({ type: "animateGLBackgroundOutComplete" });
          if (group.current) group.current.visible = false;
        },
      },
    });

  function animateIn() {
    if (visible.current) return;
    if (group.current) group.current.visible = true;
    visible.current = true;

    animateBackgroundIn();
  }

  function animateOut() {
    if (!visible.current) return;

    if (textVisible.current) animateTextOut(0);

    visible.current = false;

    animateBackgroundOut();
  }

  // function animateTextIn() {
  //   if (textVisible.current) return;
  //   textVisible.current = true;
  //   const delay = (TRANSITION_DURATION / 1000) * 1.5;
  //   bgTexts.current.forEach((r) => r?.animateIn?.({ delay }));
  // }

  // function animateTextOut(delay = (TRANSITION_DURATION / 1000) * 1.5) {
  //   if (!textVisible.current) return;

  //   textVisible.current = false;
  //   bgTexts.current.forEach((r) => r?.animateOut?.({ delay }));
  // }

  // useEvent(
  //   events.ROUTE_CHANGE_START,
  //   () => {
  //     // if (menuStore.getState().isOpen)
  //     bgTexts.current.forEach((r) => r?.animateOut?.());
  //   },
  //   true
  // );

  // useEvent(events.GL_BACKGROUND_OPEN, animateIn, true);
  // useEvent(events.GL_BACKGROUND_CLOSE, animateOut, true);

  self.onmessage = (event) => {
    if (event.data.type === "animateGLBackgroundIn") {
      animateIn();
    }

    if (event.data.type === "animateGLBackgroundOut") {
      animateOut();
    }

    if (event.data.type === "setGLBackgroundTheme") {
      setTheme(event.data.data);
    }
  };

  //useEvent(events.MENU_OPEN, animateTextIn, true);
  // useEvent(
  //   events.MENU_CLOSE,
  //   () => {
  //     animateTextOut(0);
  //   },
  //   true
  // );

  // useLoop(
  //   ({ progress }) => {
  //     if (!bgTextGroups.current.length) return;
  //     bgTextGroups.current.forEach((group, i) => {
  //       if (!group?.__width) return;
  //       const width = group.__width / 4;
  //       const isEvenMultiplier = i % 2 === 0 ? 1 : -1;
  //       const x = map(progress, 0, 1, -width, width);

  //       group.position.x = x * isEvenMultiplier;
  //     });
  //   },
  //   { initiallyActive: true, speed: 0.035 }
  // );

  useFrame(({ clock }) => {
    shader.uniforms.uTime.value = clock.elapsedTime;
  });

  const rotation = 0.1;
  const fontSize = 3.2;

  return (
    <group ref={group}>
      <mesh ref={bg} renderOrder={-1} geometry={geometry}>
        <shaderMaterial args={[shader]} />
      </mesh>
      {/* FIXME: Positioning */}
      {/* <group ref={textGroup}>
        <Flex justifyContent="space-between" flexDirection={"column"}>
          <Box
            ref={(r) => (bgTextGroups.current[0] = r)}
            height={height}
            rotation-y={rotation}
          >
            <GLText
              ref={(r) => (bgTexts.current[0] = r)}
              uniforms={textUniforms}
              weight={"Medium"}
              fontSize={fontSize}
            >
              ninenineninenine
            </GLText>
          </Box>
          <Box ref={(r) => (bgTextGroups.current[1] = r)} height={height}>
            <GLText
              ref={(r) => (bgTexts.current[1] = r)}
              uniforms={textUniforms}
              weight={"Medium"}
              fontSize={fontSize}
              rotation-y={-rotation}
            >
              creativecreativecreativecreative
            </GLText>
          </Box>
          <Box ref={(r) => (bgTextGroups.current[2] = r)} height={height}>
            <GLText
              ref={(r) => (bgTexts.current[2] = r)}
              uniforms={textUniforms}
              weight={"Medium"}
              fontSize={fontSize}
              rotation-y={rotation}
            >
              agencyagencyagencyagency
            </GLText>
          </Box>
        </Flex>
      </group> */}
    </group>
  );
});
